<template>
	<component
		:is="hasProfile ? 'div' : 'button'"
		v-bind="
			hasProfile
				? {}
				: {
						type: 'button',
						onClick: openLoginModal,
					}
		"
		:class="{'cursor-default': hasProfile}"
		class="flex shrink-0 flex-col overflow-hidden rounded-xs bg-brand-club-brown tracking-2xl text-white"
	>
		<div class="flex flex-row items-center justify-center px-3">
			<!-- size equivalent of text-6xl.
            We use fixed width to get 'grid like' layout. -->
			<v-icon icon="logo-transparent" class="my-2 mr-2 size-[2.25em]" />

			<strong
				v-if="hasProfile"
				class="col-span-2 flex flex-col items-center gap-x-2 px-2 py-1 text-center font-prenton-cond font-normal"
			>
				<span class="text-xl leading-none">
					{{ t("club-price") }}
				</span>

				<span class="text-5xl leading-none">
					{{ formatCurrency(clubPrice) }}
				</span>
			</strong>

			<strong
				v-else
				class="col-span-2 flex flex-col items-center gap-x-2 px-2 py-1 text-center font-prenton-cond font-normal"
			>
				<template v-if="pending">
					<span
						class="inline-block h-4 w-40 animate-pulse rounded bg-white/30"
					/>
				</template>

				<template v-else>
					<span class="text-xl leading-none">
						{{
							contents?.content?.club_price_upper_text ||
							t("club-price-upper-text")
						}}
					</span>

					<span class="text-5xl leading-none">
						{{
							contents?.content?.club_price_lower_text ||
							t("club-price-lower-text")
						}}
					</span>
				</template>
			</strong>
		</div>

		<div
			class="flex flex-row items-center bg-brand-club-yellow px-3 text-brand-club-brown"
		>
			<small
				class="mx-auto px-2 text-[11px] leading-relaxed font-bold whitespace-nowrap text-black"
			>
				<template v-if="hasProfile">
					{{ t("save", {amount: formatCurrency(clubSaving)}) }}
				</template>

				<template v-else-if="pending">
					<span
						class="inline-block h-2 w-24 animate-pulse rounded bg-brand-club-brown/30"
					/>
				</template>

				<template v-else>
					{{
						contents?.content?.show_by_logging_in_text ||
						t("show-by-logging-in")
					}}
				</template>
			</small>
		</div>
	</component>
</template>

<script setup lang="ts">
import {useCurrency} from "@pkg/shared/composables/use-currency";
import {useCustomerLogin} from "@pkg/shared/composables/use-customer-login";
import {useStoryblokStory} from "@pkg/shared/composables/use-storyblok-story";

defineProps<{
	clubPrice: number;
	clubSaving: number;
}>();

const {hasProfile} = useCustomerLogin();
const {$eventbus} = useNuxtApp();

const {t} = useI18n({
	useScope: "local",
});

const {data: contents, status: contentsStatus} =
	useStoryblokStory("global/club-badge");
const pending = computed(() => contentsStatus.value === "pending");

const {format: formatCurrency} = useCurrency();

function openLoginModal(event: Event) {
	$eventbus.emit("open-login-modal");
	event.preventDefault();
}
</script>

<i18n lang="json">
{
	"et": {
		"club-price": "Klubihind",
		"club-price-upper-text": "Vaata",
		"club-price-lower-text": "Klubihinda",
		"show-by-logging-in": "Kuva sisselogimisel",
		"save": "Klubieelis {amount}"
	},
	"fi": {
		"club-price": "Klubihinta",
		"club-price-upper-text": "Katso",
		"club-price-lower-text": "Klubietu",
		"show-by-logging-in": "Näytä kirjautumalla",
		"save": "Klubietusi {amount}"
	}
}
</i18n>
